import { useEffect, useState } from 'react'
import { DATEPICKER_FORMAT } from 'constants/dates'
import type { SearchOption } from '@jana-technology/platform-ui'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'

import { useGetOperatorsQuery } from 'app/services/api/operators'
import { SelectDropdownWrapper } from 'common/components/SelectDropdownWrapper'
import { useHasPermissions } from 'common/hooks/useHasPermissions'
import { mapOSRToSelectOption } from 'common/utils/filter/systemScope'

import { IPhmsaExportParams } from 'features/RegulatoryTools/PHMSA/types'

import ExportPHMSAButton from './ExportButton'

const PHMSAPage = () => {
  const hasPermissions = useHasPermissions()

  const [phmsaReportDate, setPhmsaReportDate] = useState<Date | null>(
    new Date()
  )

  const [selectedOperators, setSelectedOperators] = useState<SearchOption[]>([])

  const { currentData: operatorOptions } = useGetOperatorsQuery({})

  // Automatically set selectedOperators to all available operators from API on load
  useEffect(() => {
    setSelectedOperators(mapOSRToSelectOption(operatorOptions))
  }, [operatorOptions])

  const phmsaExportParams: IPhmsaExportParams = {
    exportDate: phmsaReportDate?.toJSON(),
    operatorList: selectedOperators.map((operator) => operator.label)
  }

  return (
    // REVIEW: Could move this into it's own component if there's more features for the PHMSA page
    <div className='flex flex-1 px-32 py-16'>
      <div className='flex flex-col'>
        <h2
          className={[
            'mb-2 font-medium',
            'uppercase tracking-wider text-lighthouseBlue'
          ].join(' ')}
        >
          Regulatory Tools
        </h2>
        <h1 className='mb-6 text-3xl font-semibold'>
          Draft DOT PHMSA Annual Report
        </h1>
        <div className='inline-block'>
          <div className='flex flex-row items-end gap-4'>
            <div className='w-[280px] flex-col'>
              <div className='pb-2 font-semibold'>Date*</div>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  className='w-full'
                  value={phmsaReportDate}
                  disableFuture
                  format={DATEPICKER_FORMAT}
                  onChange={setPhmsaReportDate}
                  slotProps={{
                    textField: {
                      sx: {
                        '& .MuiInputBase-root': { height: '40px' },
                        '& .MuiInputBase-input': {
                          padding: '0 14px !important'
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </div>
            <div className='w-[280px] flex-col'>
              <div className='pb-2 font-semibold'>Operator</div>
              <SelectDropdownWrapper
                className='w-full'
                mode='tags'
                options={mapOSRToSelectOption(operatorOptions)}
                value={selectedOperators.map(({ value }) => value)}
                onChange={(_, selections) => {
                  if (Array.isArray(selections)) {
                    setSelectedOperators(selections)
                  }
                }}
                placeholder='Select operator'
              />
            </div>
            {hasPermissions(['phmsa:export']) && (
              <ExportPHMSAButton exportParams={phmsaExportParams} />
            )}
          </div>
          <div className='mb-3 mt-1 w-[280px] text-sm italic text-gray-700'>
            Determines the dataset used for generating the report
          </div>
          <div className='w-full'>
            {/* using 728px width to match approximate width of export data table */}
            <div
              className={[
                'flex px-2 py-3',
                'w-[728px]',
                'rounded border border-blue',
                'text-blue'
              ].join(' ')}
            >
              <div>
                <InfoOutlinedIcon className='mx-2' />
              </div>
              <div className='ml-1 text-sm font-medium'>
                The values presented in this report are subject to verification.
                Please consult with JANA prior to making any decisions based on
                this information.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PHMSAPage
